import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { HttpService } from '@/features/api/http.service';
import { useMixpanel } from '@/hooks/useMixpanel';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { toastBackendError } from '@/features/api/utils/toastBackendError';
import { parseBackendError } from '@/libs/utils/parseBackendErrors';
import type { ILocationMarker, IProject, IWorkspace } from '@/types/api';
import type {
  IAddWorkspaceLocationsDTO,
  ICreateWorkspaceDTO,
  IUpdateWorkspaceDTO,
  IWorkspacePaginatedDTO,
} from '@/types/dto';

const WORKSPACES_PATH = '/workspaces';

const WORKSPACES_FACTORY_NAME = 'workspaces';

// Workspaces are a small, bounded per-user collection. We fetch the whole set
// in one request and filter/paginate client-side (no server-side search). The
// list endpoint does not clamp `limit` (see backend list_workspaces.go), so a
// generous cap covers every realistic case.
const WORKSPACES_FETCH_ALL_LIMIT = 200;

/** `GET /workspaces/{id}/locations` payload and the `locationMarkers` cache entry (one shape). */
export interface ILocationMarkersCache {
  markers: ILocationMarker[];
}

const LOCATION_MARKERS_STALE_TIME_MS = 5 * 60 * 1000;

export const workspacesKeyFactory = createQueryKeys(WORKSPACES_FACTORY_NAME, {
  getAll: () => ({
    queryKey: [WORKSPACES_PATH],
    queryFn: () =>
      HttpService.get<string, IWorkspacePaginatedDTO>(
        WORKSPACES_PATH,
        new URLSearchParams({ page: '1', limit: WORKSPACES_FETCH_ALL_LIMIT.toString() }),
      ),
  }),
  getOne: (id: string) => ({
    queryKey: [WORKSPACES_PATH, id],
    queryFn: () => HttpService.get<string, IWorkspace>(`${WORKSPACES_PATH}/${id}`),
  }),
  locationMarkers: (workspaceId: string) => ({
    queryKey: [WORKSPACES_PATH, workspaceId, 'location-markers'],
    queryFn: () =>
      HttpService.get<string, ILocationMarkersCache>(`${WORKSPACES_PATH}/${workspaceId}/locations`),
  }),
});

export async function invalidateWorkspaceMarkers(queryClient: QueryClient, workspaceId: string) {
  await queryClient.invalidateQueries({
    queryKey: workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
  });
}

/**
 * Fetches the full set of the user's workspaces in a single request. Consumers
 * (the manage panel and the map switcher) filter and paginate this list
 * client-side, so they share one cache entry and never disagree.
 */
export function useAllWorkspacesQuery() {
  return useQuery(workspacesKeyFactory.getAll());
}

// ── Optimistic-cache helpers (shared by update + delete) ──────────────────────
// The list is cached under the single `getAll` key; both mutations snapshot it,
// rewrite it optimistically, and roll back on error.

type PageSnapshot = { key: QueryKey; data: IWorkspacePaginatedDTO | undefined };

/**
 * Cancel in-flight workspace queries and snapshot every cached `getAll` page so
 * a mutation can roll back on error. Scoped to 'getAll' on purpose: the getOne
 * entry holds an `IWorkspace` (not `IWorkspacePaginatedDTO`), so including it
 * would break code that iterates `pageData.workspaces`.
 */
async function snapshotWorkspacePages(queryClient: QueryClient): Promise<PageSnapshot[]> {
  await queryClient.cancelQueries({ queryKey: [WORKSPACES_FACTORY_NAME] });
  return queryClient
    .getQueriesData<IWorkspacePaginatedDTO>({ queryKey: [WORKSPACES_FACTORY_NAME, 'getAll'] })
    .map(([key, data]) => ({ key, data }));
}

/** Rewrite the `workspaces` array of every snapshotted page via `transform`. */
function applyToWorkspacePages(
  queryClient: QueryClient,
  snapshots: PageSnapshot[],
  transform: (list: IWorkspace[]) => IWorkspace[],
) {
  for (const { key, data } of snapshots) {
    if (!data) continue;
    queryClient.setQueryData<IWorkspacePaginatedDTO>(key, {
      ...data,
      workspaces: transform(data.workspaces),
    });
  }
}

/** Restore the snapshotted pages verbatim (error rollback). */
function rollbackWorkspacePages(queryClient: QueryClient, snapshots: PageSnapshot[]) {
  for (const { key, data } of snapshots) {
    queryClient.setQueryData(key, data);
  }
}

export function useWorkspaceQuery(id: string | null) {
  return useQuery({
    ...workspacesKeyFactory.getOne(id ?? ''),
    enabled: Boolean(id),
    // A stale/unowned `?workspace=` link yields 403/404 — fail fast so the
    // surface's fallback (friendly toast + clearActive) fires immediately
    // instead of after the default 3 retries. Other failures keep one retry.
    retry: (failureCount, error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      const { httpCode } = parseBackendError(message);
      if (httpCode === 403 || httpCode === 404) return false;
      return failureCount < 1;
    },
  });
}

/**
 * Fetches a workspace's lightweight marker set (id + coordinates) so the
 * map can plot every location as a clustered pin. Disabled until a
 * workspace is active; keyed by id so switching swaps the set.
 */
export function useWorkspaceLocationMarkersQuery(workspaceId: string | null) {
  return useQuery({
    ...workspacesKeyFactory.locationMarkers(workspaceId ?? ''),
    enabled: Boolean(workspaceId),
    staleTime: LOCATION_MARKERS_STALE_TIME_MS,
    // Fail fast on 403/404 (stale/unowned link) to fire the error toast now, like useWorkspaceQuery.
    retry: (failureCount, error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      const { httpCode } = parseBackendError(message);
      if (httpCode === 403 || httpCode === 404) return false;
      return failureCount < 1;
    },
  });
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  const trackAction = useMixpanel((s) => s.trackAction);

  return useMutation({
    mutationFn: (dto: ICreateWorkspaceDTO) =>
      HttpService.post<ICreateWorkspaceDTO, IWorkspace>(WORKSPACES_PATH, dto),
    // Trust the created workspace returned by the POST response and splice it
    // into the cached list directly, instead of invalidating and refetching
    // from GET /workspaces: that list read hits the read replica, which can
    // still lag behind the write we just made (read-your-writes race —
    // the just-created workspace briefly missing from the refetched list).
    onSuccess: async (created) => {
      const pages = await snapshotWorkspacePages(queryClient);
      applyToWorkspacePages(queryClient, pages, (list) => [created, ...list]);
      toast.success('Workspace created');
      trackAction(ACTION_TRACKED.WORKSPACE_CREATED, { workspace_id: created.id });
    },
    onError: (err: unknown) => {
      toastBackendError(err);
    },
  });
}

export function useUpdateWorkspace() {
  const queryClient = useQueryClient();
  const trackAction = useMixpanel((s) => s.trackAction);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateWorkspaceDTO }) =>
      HttpService.patch<IUpdateWorkspaceDTO, IWorkspace>(`${WORKSPACES_PATH}/${id}`, data),
    onMutate: async ({ id, data: dto }) => {
      const previousPages = await snapshotWorkspacePages(queryClient);
      applyToWorkspacePages(queryClient, previousPages, (list) =>
        list.map((ws) => (ws.id === id ? { ...ws, ...dto } : ws)),
      );

      // getOne is update-specific: keep it inline.
      const previousOne = queryClient.getQueryData<IWorkspace>(
        workspacesKeyFactory.getOne(id).queryKey,
      );
      if (previousOne) {
        queryClient.setQueryData<IWorkspace>(workspacesKeyFactory.getOne(id).queryKey, {
          ...previousOne,
          ...dto,
        });
      }

      return { previousPages, previousOne };
    },
    onError: (err: unknown, { id }, context) => {
      if (context?.previousPages) {
        rollbackWorkspacePages(queryClient, context.previousPages);
      }
      if (context?.previousOne) {
        queryClient.setQueryData(workspacesKeyFactory.getOne(id).queryKey, context.previousOne);
      }
      toastBackendError(err);
    },
    // Trust the onMutate patch — it already applied the exact DTO the PATCH
    // just persisted. Re-fetching here would read the list from the (possibly
    // lagging) read replica and could momentarily revert the rename.
    onSuccess: (_data, { id }) => {
      toast.success('Workspace updated');
      trackAction(ACTION_TRACKED.WORKSPACE_UPDATED, { workspace_id: id });
    },
  });
}

export function useDeleteWorkspace() {
  const queryClient = useQueryClient();
  const trackAction = useMixpanel((s) => s.trackAction);

  return useMutation({
    mutationFn: (id: string) => HttpService.delete<undefined, void>(`${WORKSPACES_PATH}/${id}`),
    onMutate: async (id) => {
      const previousPages = await snapshotWorkspacePages(queryClient);
      applyToWorkspacePages(queryClient, previousPages, (list) =>
        list.filter((ws) => ws.id !== id),
      );
      return { previousPages };
    },
    onError: (err: unknown, _id, context) => {
      if (context?.previousPages) {
        rollbackWorkspacePages(queryClient, context.previousPages);
      }
      toastBackendError(err);
    },
    onSuccess: (_data, id) => {
      // Evict the deleted workspace's getOne entry so a later read cannot serve
      // a stale (now-404) record.
      queryClient.removeQueries({ queryKey: workspacesKeyFactory.getOne(id).queryKey });
      // Trust the onMutate removal — invalidating the list here would refetch
      // from the read replica, which can still return the just-deleted
      // workspace if replication lags, reinstating it in the UI.
      toast.success('Workspace deleted');
      trackAction(ACTION_TRACKED.WORKSPACE_DELETED, { workspace_id: id });
    },
  });
}

/**
 * Mutation hook for adding locations to a workspace.
 *
 * - `mutationFn`: POST /workspaces/{workspaceId}/locations → IProject (202)
 * - `onMutate`: cancel + snapshot the locationMarkers cache for the passed
 *   workspaceId; optimistically append one ILocationMarker per tempId using an
 *   immutable clone. Cache writes are keyed to the passed workspaceId only
 *   (workspace-switch safety).
 * - `onError`: rollback the markers cache to the snapshot; toast.error.
 *   Drafts are NOT cleared — the caller keeps them for retry.
 * - `onSuccess`: toast.success. Markers are NOT invalidated here; reconciliation
 *   happens on the SSE terminal to avoid dropping the optimistic pins early.
 */
export function useAddWorkspaceLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      data,
    }: {
      workspaceId: string;
      data: IAddWorkspaceLocationsDTO;
      tempIds: string[];
      /** Overrides the success toast copy (e.g. a radius re-compute is not an "add"). */
      successMessage?: string;
    }) =>
      HttpService.post<IAddWorkspaceLocationsDTO, IProject>(
        `${WORKSPACES_PATH}/${workspaceId}/locations`,
        data,
      ),

    onMutate: async ({ workspaceId, data, tempIds }) => {
      const markersQueryKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;

      // 1. Cancel in-flight locationMarkers queries to avoid overwriting optimistic state
      await queryClient.cancelQueries({ queryKey: markersQueryKey });

      // 2. Snapshot the current markers cache for rollback on error
      const previousMarkers = queryClient.getQueryData<ILocationMarkersCache>(markersQueryKey);

      // 3. Build optimistic markers — one per tempId, coordinates from DTO locations[i]
      const newMarkers: ILocationMarker[] = tempIds.map((tempId, i) => ({
        id: tempId,
        latitude: data.locations[i].coordinates.latitude,
        longitude: data.locations[i].coordinates.longitude,
      }));

      // 4. Immutably append optimistic markers to the cache
      const existingMarkers = previousMarkers?.markers ?? [];
      queryClient.setQueryData<ILocationMarkersCache>(markersQueryKey, {
        markers: [...existingMarkers, ...newMarkers],
      });

      return { previousMarkers };
    },

    onError: (err: unknown, { workspaceId }, context) => {
      // Rollback the markers cache to the pre-mutation snapshot
      const markersQueryKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;
      queryClient.setQueryData<ILocationMarkersCache>(markersQueryKey, context?.previousMarkers);

      toastBackendError(err);
    },

    onSuccess: (_data, { successMessage }) => {
      // Markers are NOT invalidated here: reconciliation happens on the SSE
      // terminal (useWorkspaceCompute) to avoid dropping the optimistic pins.
      toast.success(successMessage ?? 'Locations added — computing metrics…');
    },
  });
}

/**
 * Mutation hook for uploading a CSV file of locations to a workspace.
 *
 * - `mutationFn`: POST multipart/form-data to /workspaces/{workspaceId}/locations → IProject (202)
 * - No optimistic marker-cache writes — reconciliation happens on the SSE
 *   terminal (same as `useAddWorkspaceLocations`).
 * - `onSuccess`: toast.success.
 * - `onError`: toast.error via toastBackendError.
 */
export function useUploadWorkspaceCSV() {
  return useMutation({
    mutationFn: ({
      workspaceId,
      formData,
      onProgress,
    }: {
      workspaceId: string;
      formData: FormData;
      onProgress?: (progress: number) => void;
    }) =>
      HttpService.postWithMultiPart<FormData, IProject>(
        `${WORKSPACES_PATH}/${workspaceId}/locations`,
        formData,
        undefined,
        onProgress,
      ),
    onSuccess: () => {
      toast.success('Locations uploaded — computing metrics…');
    },
    onError: (err: unknown) => {
      toastBackendError(err);
    },
  });
}

/**
 * Mutation hook for removing a saved location from a workspace.
 *
 * - `mutationFn`: DELETE /workspaces/{workspaceId}/locations/{locationId} → 204 void
 * - `onMutate`: cancel + snapshot `locationMarkers(workspaceId)`; optimistically
 *   remove the marker with an immutable filter. Also decrements the target
 *   workspace's `location_count` by one in both the `getAll` pages and
 *   `getOne` cache (reusing the `useUpdateWorkspace`/`useDeleteWorkspace`
 *   snapshot helpers) so the switcher's count reflects the removal
 *   immediately — invalidating here instead would refetch from the read
 *   replica, which can still return the pre-removal count if replication
 *   lags. Cache writes are keyed to the passed workspaceId only
 *   (workspace-switch safety).
 * - `onError`: rollback the markers cache and the `location_count` patch to
 *   the snapshot; toast.error.
 * - `onSuccess`: trust the onMutate decrement; confirm with a toast (the
 *   caller gates the mutation behind a confirmation modal — there is no
 *   undo). Emits `LOCATION_REMOVED` analytics event `{ location_id }` —
 *   project-agnostic. Pass `silent: true` when the removal is an
 *   implementation detail of another flow (e.g. a radius recompute deleting
 *   the superseded location) rather than a user-initiated delete — this
 *   skips the toast/analytics event but keeps the count decrement.
 */
/**
 * Drops one marker from a workspace's cached marker set.
 *
 * Used when the location left the workspace without this client asking — a location that carries a
 * deal is excluded by the markers read itself, so the cache is corrected rather than a mutation
 * replayed. The removal mutation below uses it too, after taking its rollback snapshot.
 */
export function dropWorkspaceLocationMarker(
  queryClient: QueryClient,
  workspaceId: string,
  locationId: string,
): void {
  const markersQueryKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;
  const existing = queryClient.getQueryData<ILocationMarkersCache>(markersQueryKey);
  queryClient.setQueryData<ILocationMarkersCache>(markersQueryKey, {
    markers: (existing?.markers ?? []).filter((marker) => marker.id !== locationId),
  });
}

export function useRemoveWorkspaceLocation() {
  const queryClient = useQueryClient();
  const trackAction = useMixpanel((s) => s.trackAction);

  return useMutation({
    mutationFn: ({
      workspaceId,
      locationId,
    }: {
      workspaceId: string;
      locationId: string;
      silent?: boolean;
    }) =>
      HttpService.delete<undefined, void>(
        `${WORKSPACES_PATH}/${workspaceId}/locations/${locationId}`,
      ),

    onMutate: async ({ workspaceId, locationId }) => {
      const markersQueryKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;

      // 1. Cancel in-flight locationMarkers queries to avoid overwriting optimistic state
      await queryClient.cancelQueries({ queryKey: markersQueryKey });

      // 2. Snapshot the current markers cache for rollback on error / Undo
      const previousMarkers = queryClient.getQueryData<ILocationMarkersCache>(markersQueryKey);

      // 3. Immutably remove the targeted marker from the cache
      dropWorkspaceLocationMarker(queryClient, workspaceId, locationId);

      // 4. Decrement location_count on the target workspace only, in getAll
      // and getOne, mirroring useUpdateWorkspace's optimistic patch.
      const previousPages = await snapshotWorkspacePages(queryClient);
      applyToWorkspacePages(queryClient, previousPages, (list) =>
        list.map((ws) =>
          ws.id === workspaceId ? { ...ws, location_count: ws.location_count - 1 } : ws,
        ),
      );

      const previousOne = queryClient.getQueryData<IWorkspace>(
        workspacesKeyFactory.getOne(workspaceId).queryKey,
      );
      if (previousOne) {
        queryClient.setQueryData<IWorkspace>(workspacesKeyFactory.getOne(workspaceId).queryKey, {
          ...previousOne,
          location_count: previousOne.location_count - 1,
        });
      }

      return { previousMarkers, previousPages, previousOne };
    },

    onError: (err: unknown, { workspaceId }, context) => {
      // Rollback the markers cache and the location_count patch to the pre-mutation snapshot
      const markersQueryKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;
      queryClient.setQueryData<ILocationMarkersCache>(markersQueryKey, context?.previousMarkers);

      if (context?.previousPages) {
        rollbackWorkspacePages(queryClient, context.previousPages);
      }
      if (context?.previousOne) {
        queryClient.setQueryData(
          workspacesKeyFactory.getOne(workspaceId).queryKey,
          context.previousOne,
        );
      }

      toastBackendError(err);
    },

    onSuccess: (_data, { locationId, silent }) => {
      if (silent) return;
      toast.success('Location removed');
      trackAction(ACTION_TRACKED.LOCATION_REMOVED, { location_id: locationId });
    },
  });
}
